import Button from '@/common/components/Button'
import ButtonLink from '@/common/components/ButtonLink'
import { TicketData } from '@/common/types/support/ticket.type'
import { formatDateTime } from '@/common/utils/formatters'
import { SupportTicketStatus } from '@/features/support/internal/components/SupportTicketStatus'
import { SupportTicketUser } from '@/features/support/internal/components/SupportTicketUser'
import { relationTypeStrategy } from '@/features/support/internal/constants/relationTypeOptions.constant'
import { supportStatusStrategy } from '@/features/support/internal/constants/supportStatusOptions.constant'
import { supportTicketTypeStrategy } from '@/features/support/internal/constants/supportTicketTypeOptions.constant'
import { SupportTicketAvailableUserWithRelationType } from '@/features/support/internal/types/supportTicketAvailableUser.type'
import { ArrowBigRightIcon, TrashIcon } from 'lucide-react'
import { createElement, Dispatch, SetStateAction } from 'react'
import { Link } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'

export class InternalSupportTicketsTransformer {
  static tableTransformer() {
    return (supportTickets: TicketData[]) => {
      return supportTickets.map((supportTicket) => ({
        _className: supportTicket.status === 'awaiting_tcard_response' && 'bg-yellow-op-2',
        reference_number: supportTicket.reference_number,
        status: supportTicket.status
          ? createElement(SupportTicketStatus, { ticketType: supportTicket.ticket_type, status: supportTicket.status })
          : '',
        reason: supportTicket.reason.display_name || '',
        subject_line: supportTicket.subject_line || '',
        creator: supportTicket.creator ? (
          <ButtonLink
            linkTo={`/users/${supportTicket.creator.id}`}
            operationKlassToCheckPermission='Admin::Users::Show'
            dataTestId="creator_clickable_link"
          >
            {createElement(SupportTicketUser, { user: supportTicket.creator })}
          </ButtonLink>
        ) : '',
        responsible: supportTicket.responsible ? (
          <ButtonLink
            linkTo={`/users/${supportTicket.responsible.id}`}
            operationKlassToCheckPermission='Admin::Users::Show'
            dataTestId="responsible_clickable_link"
          >
            {createElement(SupportTicketUser, { user: supportTicket.responsible })}
          </ButtonLink>
        ) : '',
        created_at: supportTicket.created_at ? formatDateTime(new Date(supportTicket.created_at)) : '',
        actions: (
          <Link to={`/internal-support/${supportTicket.id}`}>
            <Button
              className="bg-transparent hover:bg-transparent p-0"
              operationKlassToCheckPermission="Admin::Support::Tickets::Show"
            >
              <ArrowBigRightIcon />
            </Button>
          </Link>
        ),
      }))
    }
  }

  static usersTableTransformer() {
    return (
      users: SupportTicketAvailableUserWithRelationType[],
      setUsers?: Dispatch<SetStateAction<SupportTicketAvailableUserWithRelationType[]>>,
    ) => {
      const sortedUsers = users.sort((a, b) =>
        a.relationType === 'responsible' && b.relationType !== 'responsible'
          ? -1
          : a.relationType !== 'responsible' && b.relationType === 'responsible'
          ? 1
          : 0,
      )

      return sortedUsers.map((user) => ({
        full_name: (
          <ButtonLink
            linkTo={`/users/${user.id}`}
            operationKlassToCheckPermission='Admin::Users::Show'
            dataTestId="responsible_clickable_link"
          >
            {user.full_name}
          </ButtonLink>
        ),
        relation_type: relationTypeStrategy[user.relationType as keyof typeof relationTypeStrategy] || '',
        actions: (
          <div className="flex w-full justify-center">
            <Button
              variant="outline-error"
              className="p-0"
              onClick={() => setUsers?.((prev) => prev.filter((u) => u.id !== user.id))}
            >
              <TrashIcon className="size-5 shrink-0 text-danger" />
            </Button>
          </div>
        ),
      }))
    }
  }

  static supportTicketsTableTransformer() {
    return ({
      supportTickets,
      setSupportTickets,
      actionType,
    }: {
      supportTickets: TicketData[]
      setSupportTickets?: Dispatch<SetStateAction<TicketData[]>>
      actionType?: 'link' | 'remove'
    }) => {
      return supportTickets.map((supportTicket) => ({
        reference_number: supportTicket.reference_number,
        ticket_type:
          supportTicketTypeStrategy[supportTicket.ticket_type as keyof typeof supportTicketTypeStrategy] || '',
        status: supportTicket.status
          ? createElement(SupportTicketStatus, { ticketType: supportTicket.ticket_type, status: supportTicket.status })
          : '',
        reason: (
          <>
            <p data-tooltip-id={`tooltip-${supportTicket.id}-reason`}>{supportTicket.reason.display_name || ''}</p>
            <Tooltip id={`tooltip-${supportTicket.id}-reason`} className="z-50">
              <p className="text-sm font-bold">Motivo</p>
              <p className="text-xs font-light">{supportTicket.reason.description || ''}</p>
            </Tooltip>
          </>
        ),
        creator: supportTicket.creator ? createElement(SupportTicketUser, { user: supportTicket.creator }) : '',
        created_at: supportTicket.created_at ? formatDateTime(new Date(supportTicket.created_at)) : '',
        actions: (
          <div className="flex w-full justify-center">
            {actionType === 'remove' ? (
              <Button
                variant="outline-error"
                className="p-0"
                onClick={() => setSupportTickets?.((prev) => prev.filter((u) => u.id !== supportTicket.id))}
              >
                <TrashIcon className="size-5 shrink-0 text-danger" />
              </Button>
            ) : actionType === 'link' ? (
              <Link
                to={`/${supportTicket.ticket_type === 'internal' ? 'internal' : 'external'}-support/${
                  supportTicket.id
                }`}
              >
                <Button
                  className="bg-transparent hover:bg-transparent p-0"
                  operationKlassToCheckPermission="Admin::Support::Tickets::Show"
                >
                  <ArrowBigRightIcon />
                </Button>
              </Link>
            ) : null}
          </div>
        ),
      }))
    }
  }

  static quickSearchModelTransformer() {
    return (supportTickets: TicketData[]) => {
      return supportTickets.map((supportTicket) => ({
        route: `/internal-support/${supportTicket.id}`,
        items: [
          ['Código', supportTicket.reference_number],
          [
            'Status',
            supportTicket.status
              ? supportStatusStrategy[supportTicket.status as keyof typeof supportStatusStrategy]
              : '',
          ],
          ['Motivo', supportTicket.reason.display_name],
          ['Assunto', supportTicket.subject_line],
          [
            'Autor',
            `${supportTicket.creator.full_name} (${supportTicket.creator?.company?.general_information?.fantasy_name})`,
          ],
        ],
      }))
    }
  }
}
